import { useEffect, useState, type ReactNode } from "react";
import { RefreshCw, Upload, Download, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getInvokeErrorText } from "@/lib/invokeError";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";

interface CalendarSyncDropdownProps {
  workspaceId: string;
  hasGoogleCalendar: boolean;
  conflictCount?: number;
  autoSyncing?: boolean;
  onSyncComplete?: () => void;
}

type CalendarSyncStatus = {
  calendarId: string;
  displayName: string | null;
  lastSyncedAt: string | null;
  lastSyncStatus: string | null;
};

async function loadCalendarStatuses(workspaceId: string): Promise<CalendarSyncStatus[]> {
  if (isGcalMultiCalendarEnabled()) {
    const [{ data: calendars }, { data: syncStates }] = await Promise.all([
      supabase
        .from("google_calendar_sync_calendars")
        .select("calendar_id, display_name")
        .eq("workspace_id", workspaceId)
        .eq("is_enabled", true)
        .order("created_at", { ascending: true }),
      supabase
        .from("google_calendar_sync_state")
        .select("calendar_id, last_synced_at, last_sync_status")
        .eq("workspace_id", workspaceId),
    ]);

    const stateByCalendar = new Map(
      (syncStates ?? []).map((s) => [s.calendar_id, s]),
    );

    return (calendars ?? []).map((cal) => {
      const state = stateByCalendar.get(cal.calendar_id);
      return {
        calendarId: cal.calendar_id,
        displayName: cal.display_name,
        lastSyncedAt: state?.last_synced_at ?? null,
        lastSyncStatus: state?.last_sync_status ?? null,
      };
    });
  }

  const { data: credentials } = await supabase
    .from("google_calendar_credentials")
    .select("calendar_id, calendar_display_name")
    .eq("workspace_id", workspaceId)
    .eq("is_active", true)
    .maybeSingle();

  if (!credentials) return [];

  const calendarId = credentials.calendar_id || "primary";
  const { data: syncState } = await supabase
    .from("google_calendar_sync_state")
    .select("last_synced_at, last_sync_status")
    .eq("workspace_id", workspaceId)
    .eq("calendar_id", calendarId)
    .maybeSingle();

  return [{
    calendarId,
    displayName: credentials.calendar_display_name ?? null,
    lastSyncedAt: syncState?.last_synced_at ?? null,
    lastSyncStatus: syncState?.last_sync_status ?? null,
  }];
}

function formatLastSync(iso: string | null): string {
  if (!iso) return "Ainda sem sincronização";
  return `Última sync: ${new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

export function CalendarSyncDropdown({
  workspaceId,
  hasGoogleCalendar,
  conflictCount = 0,
  autoSyncing = false,
  onSyncComplete,
}: CalendarSyncDropdownProps) {
  const [syncingAction, setSyncingAction] = useState<string | null>(null);
  const [calendarStatuses, setCalendarStatuses] = useState<CalendarSyncStatus[]>([]);
  const { toast } = useToast();

  const multiCalendar = isGcalMultiCalendarEnabled();

  useEffect(() => {
    if (!hasGoogleCalendar || !workspaceId) return;
    const loadStatus = async () => {
      const statuses = await loadCalendarStatuses(workspaceId);
      setCalendarStatuses(statuses);
    };
    void loadStatus();
  }, [workspaceId, hasGoogleCalendar, syncingAction, autoSyncing]);

  if (!hasGoogleCalendar) return null;

  const primaryStatus = calendarStatuses[0];
  const lastSyncedAt = primaryStatus?.lastSyncedAt ?? null;
  const lastSyncStatus = primaryStatus?.lastSyncStatus ?? null;

  const handleSync = async (
    action: "sync_to_google" | "sync_from_google" | "full_sync",
    calendarId?: string,
  ) => {
    const actionKey = calendarId ? `${action}:${calendarId}` : action;
    setSyncingAction(actionKey);
    try {
      const { data, error } = await supabase.functions.invoke("google-calendar-sync", {
        body: {
          action,
          workspaceId,
          ...(calendarId ? { calendarId } : {}),
        },
      });

      if (error) throw error;

      if (data?.error === "GOOGLE_AUTH_NEEDS_RECONNECT") {
        toast({
          title: "Reconexão necessária",
          description: "A conexão com o Google Calendar precisa ser refeita.",
          variant: "destructive",
        });
        return;
      }

      if (data?.error === "SYNC_ALREADY_RUNNING" || data?.error === "TOKEN_REFRESH_IN_PROGRESS") {
        toast({
          title: "Sincronização em andamento",
          description: "Já existe uma sincronização do Google Calendar rodando.",
        });
        return;
      }

      const parts: string[] = [];
      if (data?.imported) parts.push(`${data.imported} importados`);
      if (data?.exported) parts.push(`${data.exported} exportados`);
      if (data?.reconciled) parts.push(`${data.reconciled} reconciliados`);
      if (data?.conflicted) parts.push(`${data.conflicted} conflitos`);

      toast({
        title: "Sincronização concluída",
        description: parts.length ? parts.join(", ") : "Nenhuma alteração detectada",
      });

      onSyncComplete?.();
      const statuses = await loadCalendarStatuses(workspaceId);
      setCalendarStatuses(statuses);
    } catch (err: unknown) {
      console.error("Sync error:", err);
      const rawMessage = getInvokeErrorText(err);
      const message = rawMessage.includes("non-2xx")
        ? "Não foi possível sincronizar agora. Tente novamente em alguns instantes."
        : rawMessage || "Tente novamente";
      toast({
        title: "Erro na sincronização",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSyncingAction(null);
    }
  };

  const isLoading = !!syncingAction || autoSyncing;
  const lastSyncLabel = formatLastSync(lastSyncedAt);

  const renderSyncItem = (
    action: "sync_to_google" | "sync_from_google" | "full_sync",
    label: string,
    description: string,
    icon: ReactNode,
    calendarId?: string,
  ) => {
    const actionKey = calendarId ? `${action}:${calendarId}` : action;
    return (
      <DropdownMenuItem
        key={actionKey}
        onClick={() => handleSync(action, calendarId)}
        disabled={isLoading}
        className="gap-2"
      >
        {icon}
        <div className="flex flex-col">
          <span>{label}</span>
          <span className="text-xs text-muted-foreground">{description}</span>
        </div>
        {syncingAction === actionKey && <Loader2 className="ml-auto h-3 w-3 animate-spin" />}
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 relative" disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">Sincronizar</span>
          {conflictCount > 0 && (
            <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] px-1 text-xs">
              {conflictCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {multiCalendar && calendarStatuses.length > 1 ? (
          calendarStatuses.map((cal, index) => (
            <div key={cal.calendarId}>
              {index > 0 && <DropdownMenuSeparator />}
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                <div className="font-medium text-foreground">
                  {cal.displayName || cal.calendarId}
                </div>
                <div>{formatLastSync(cal.lastSyncedAt)}</div>
                {cal.lastSyncStatus && cal.lastSyncStatus !== "success" && (
                  <div className="text-yellow-600">Status: {cal.lastSyncStatus}</div>
                )}
              </div>
              {renderSyncItem(
                "sync_from_google",
                "Buscar do Google",
                "Importa eventos desta agenda",
                <Download className="h-4 w-4" />,
                cal.calendarId,
              )}
            </div>
          ))
        ) : (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            <div>{lastSyncLabel}</div>
            {lastSyncStatus && lastSyncStatus !== "success" && (
              <div className="text-yellow-600">Status: {lastSyncStatus}</div>
            )}
            {autoSyncing && <div>Atualizando em segundo plano...</div>}
          </div>
        )}

        <DropdownMenuSeparator />

        {renderSyncItem(
          "sync_to_google",
          "Enviar para o Google",
          multiCalendar ? "Exporta agendamentos (agenda primária)" : "Exporta agendamentos locais",
          <Upload className="h-4 w-4" />,
        )}

        {renderSyncItem(
          "sync_from_google",
          "Buscar do Google",
          multiCalendar ? "Importa todas as agendas habilitadas" : "Importa eventos do Google",
          <Download className="h-4 w-4" />,
        )}

        <DropdownMenuSeparator />

        {renderSyncItem(
          "full_sync",
          "Sincronização completa",
          multiCalendar ? "Importar + exportar todas as agendas" : "Importar + reconciliar + exportar",
          <RefreshCw className="h-4 w-4" />,
        )}

        {conflictCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-yellow-500" />
              {conflictCount} conflito{conflictCount > 1 ? "s" : ""} pendente{conflictCount > 1 ? "s" : ""}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
